import {
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';

export interface PreparedMmcEmail {
  to: string;
  subject: string;
  text: string;
  html: string;
  sourceRows: number[];
  language: 'nb' | 'en';
  outcome: string;
  participantCount: number;
  recipientName: string;
}

export interface MmcContentTemplate {
  norwegianBody: string;
  englishBody: string;
  updatedAt: Date | null;
}

const MMC_CONTENT_TEMPLATE_REF = doc(db, 'adminContent', 'mmc-post-event-poc');

export const loadMmcContentTemplate = async (): Promise<MmcContentTemplate | null> => {
  const snapshot = await getDoc(MMC_CONTENT_TEMPLATE_REF);
  if (!snapshot.exists()) return null;
  const data = snapshot.data();
  if (typeof data.norwegianBody !== 'string' || typeof data.englishBody !== 'string') return null;
  return {
    norwegianBody: data.norwegianBody,
    englishBody: data.englishBody,
    updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : null,
  };
};

export const saveMmcContentTemplate = async (
  norwegianBody: string,
  englishBody: string
): Promise<void> => {
  await setDoc(MMC_CONTENT_TEMPLATE_REF, {
    norwegianBody,
    englishBody,
    updatedAt: serverTimestamp(),
    version: 1,
  }, { merge: true });
};

interface QueueOptions {
  startAt?: Date;
  intervalSeconds?: number;
  batchLabel?: string;
  onProgress?: (queued: number, total: number) => void;
  marketing?: boolean;
}

const MMC_UNSUBSCRIBE_BASE = 'https://runnershub.krultra.no/api/unsubscribe/mmc';
const MMC_UNSUBSCRIBE_ADDRESS = 'unsubscribe@mmctrail.no';

const newUnsubscribeToken = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
};

const addUnsubscribeFooter = (email: PreparedMmcEmail, unsubscribeUrl: string): Pick<PreparedMmcEmail, 'html' | 'text'> => {
  const label = email.language === 'nb' ? 'Avmeld fremtidige nyheter og tilbud fra MMC' : 'Unsubscribe from future MMC news and offers';
  const htmlFooter = `<br><a href="${unsubscribeUrl}" style="color:#fff;text-decoration:underline;">${label}</a>`;
  return { html: email.html.replace('<!-- MMC_UNSUBSCRIBE_FOOTER -->', htmlFooter), text: `${email.text.trim()}\n\n${label}:\n${unsubscribeUrl}` };
};

const newBatchId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `mmc-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const mailDocument = (
  email: PreparedMmcEmail,
  batchId: string,
  sendAfter: Date,
  isTest: boolean,
  marketing: boolean,
  unsubscribeUrl?: string,
  unsubscribeToken?: string
) => {
  const content = marketing && unsubscribeUrl ? addUnsubscribeFooter(email, unsubscribeUrl) : { html: email.html.replace('<!-- MMC_UNSUBSCRIBE_FOOTER -->', ''), text: email.text };
  return ({
  to: email.to,
  senderProfile: 'mmc',
  subject: email.subject,
  message: {
    subject: email.subject,
    text: content.text,
    html: content.html,
  },
  campaignType: marketing ? 'mmc_marketing' : 'transactional',
  ...(marketing && unsubscribeUrl && unsubscribeToken ? {
    unsubscribeUrl,
    unsubscribeMailto: `mailto:${MMC_UNSUBSCRIBE_ADDRESS}?subject=${encodeURIComponent(`Unsubscribe ${unsubscribeToken}`)}`,
    listId: 'MMC updates <mmc-marketing.mmctrail.no>',
  } : {}),
  sendAfter: Timestamp.fromDate(sendAfter),
  smtpAgent: {
    state: 'PENDING' as const,
    lastUpdatedAt: serverTimestamp(),
  },
  state: 'PENDING' as const,
  delivery: null,
  status: 'pending',
  createdAt: serverTimestamp(),
  type: isTest ? 'mmc_post_event_test' : 'mmc_post_event',
  metadata: {
    emailType: isTest ? 'mmc_post_event_test' : 'mmc_post_event',
    event: 'MMC 2026',
    source: 'racetracker_participant_file_poc',
    batchId,
    sourceRows: email.sourceRows,
    language: email.language,
    outcome: email.outcome,
    participantCount: email.participantCount,
    recipientName: email.recipientName,
    isTest,
  },
  });
};

export const queueMmcTestEmail = async (
  email: PreparedMmcEmail,
  marketing = true
): Promise<string> => {
  const batchId = `test-${newBatchId()}`;
  const batch = writeBatch(db);
  const ref = doc(collection(db, 'mail'));
  const token = marketing ? newUnsubscribeToken() : undefined;
  const unsubscribeUrl = token ? `${MMC_UNSUBSCRIBE_BASE}/${encodeURIComponent(token)}?lang=${email.language}` : undefined;
  batch.set(ref, mailDocument(email, batchId, new Date(), true, marketing, unsubscribeUrl, token));
  if (token) batch.set(doc(db, 'mmcUnsubscribeTokens', token), { email: email.to.trim().toLocaleLowerCase('nb-NO'), scope: 'mmc_marketing', language: email.language, batchId, isTest: true, createdAt: serverTimestamp() });
  await batch.commit();
  return ref.id;
};

export const queueMmcEmails = async (
  emails: PreparedMmcEmail[],
  options: QueueOptions = {}
): Promise<string> => {
  if (!emails.length) throw new Error('Ingen valgte mottakere.');
  const intervalSeconds = options.intervalSeconds ?? 60;
  const marketing = options.marketing ?? true;
  if (intervalSeconds < 60) {
    throw new Error('MMC-meldinger kan ikke planlegges tettere enn 60 sekunder.');
  }

  const startAt = options.startAt ?? new Date(Date.now() + 2 * 60 * 1000);
  const batchId = options.batchLabel?.trim() || newBatchId();
  const firestoreBatchSize = 100;

  for (let offset = 0; offset < emails.length; offset += firestoreBatchSize) {
    const firestoreBatch = writeBatch(db);
    const slice = emails.slice(offset, offset + firestoreBatchSize);
    slice.forEach((email, index) => {
      const absoluteIndex = offset + index;
      const sendAfter = new Date(
        startAt.getTime() + absoluteIndex * intervalSeconds * 1000
      );
      const ref = doc(collection(db, 'mail'));
      const token = marketing ? newUnsubscribeToken() : undefined;
      const unsubscribeUrl = token ? `${MMC_UNSUBSCRIBE_BASE}/${encodeURIComponent(token)}?lang=${email.language}` : undefined;
      firestoreBatch.set(ref, mailDocument(email, batchId, sendAfter, false, marketing, unsubscribeUrl, token));
      if (token) firestoreBatch.set(doc(db, 'mmcUnsubscribeTokens', token), { email: email.to.trim().toLocaleLowerCase('nb-NO'), scope: 'mmc_marketing', language: email.language, batchId, isTest: false, createdAt: serverTimestamp() });
    });
    await firestoreBatch.commit();
    options.onProgress?.(Math.min(offset + slice.length, emails.length), emails.length);
  }

  return batchId;
};
